import { useEffect, useMemo, useState } from 'react';
import { api, ErrorApi, type GrillaAdmin, type ServicioAdmin, type Tramo } from './api';
import { AvisoError, Boton, Skeleton, Vacio, Tarjeta } from './ui';

/**
 * Diseño 6a — precios y márgenes.
 *
 * Consola interna, densa a propósito. Es una consola, no una vitrina.
 *
 * ── LO QUE SOSTIENE ESTA PANTALLA ───────────────────────────────────────────────
 *
 * **Cuando no conocemos el costo, el margen sale VACÍO. Nunca cero, nunca un guion que se
 * parezca a cero.**
 *
 * Un margen calculado sobre un costo inventado es peor que no tener margen: alguien fija un
 * precio con ese número y vende bajo costo durante meses sin que nadie lo note. Se descubre
 * cuadrando la caja, cuando ya vendiste cien mil consultas perdiendo plata en cada una.
 *
 * Y todo se edita aquí, sin desplegar. En el sistema anterior los precios vivían escritos en
 * el código: cambiar uno exigía un programador y un despliegue. Un negocio no puede depender
 * de eso para ajustar su lista de precios.
 */

const TRAMOS: Tramo[] = ['BASE', 'PRO', 'ESCALA', 'CORPORATIVO'];

interface Edicion {
  serviceKey: string;
  tier: Tramo;
  valor: string;
}

/**
 * Tres estados de carga, no dos.
 *
 * `cargando` y `listo` son los obvios. El tercero —`sinPermiso`— es el que siempre se olvida
 * y el que más molesta: sin él, un operador que entra aquí ve un skeleton girando para
 * siempre, cree que el sistema está roto, y llama a soporte por algo que funciona perfecto.
 */
type Estado =
  | { fase: 'cargando' }
  | { fase: 'listo'; grilla: GrillaAdmin }
  | { fase: 'sinPermiso' }
  | { fase: 'error'; mensaje: string };

export function Admin() {
  const [estado, setEstado] = useState<Estado>({ fase: 'cargando' });
  const [ediciones, setEdiciones] = useState<Edicion[]>([]);
  const [costos, setCostos] = useState<Record<string, string>>({});
  const [guardando, setGuardando] = useState(false);
  const [avisoGuardado, setAvisoGuardado] = useState<string | null>(null);

  const grilla = estado.fase === 'listo' ? estado.grilla : null;

  useEffect(() => {
    api
      .grillaAdmin('PEN')
      .then((g) => setEstado({ fase: 'listo', grilla: g }))
      .catch((e: unknown) => {
        if (e instanceof ErrorApi && e.status === 403) {
          setEstado({ fase: 'sinPermiso' });
          return;
        }
        setEstado({
          fase: 'error',
          mensaje: e instanceof ErrorApi ? e.message : 'No se pudo cargar la grilla.',
        });
      });
  }, []);

  const sinCosto = useMemo(
    () => (grilla?.services ?? []).filter((s) => !s.costKnown),
    [grilla],
  );

  /**
   * Precios por debajo del costo.
   *
   * Se calcula en vivo sobre lo editado, no sobre lo guardado. Avisar DESPUÉS de guardar es
   * avisar tarde: para entonces el precio malo ya está cobrando.
   */
  const bajoCosto = useMemo(() => {
    if (!grilla) return [];
    const malos: { servicio: string; tramo: Tramo; precio: number; costo: number }[] = [];

    for (const s of grilla.services) {
      if (s.cost === null) continue;
      const costo = Number(s.cost);
      for (const t of TRAMOS) {
        const editado = ediciones.find((e) => e.serviceKey === s.key && e.tier === t);
        const bruto = editado?.valor ?? s.prices[t]?.price;
        if (bruto === null || bruto === undefined || bruto === '') continue;
        const precio = Number(bruto);
        if (Number.isFinite(precio) && precio > 0 && precio < costo) {
          malos.push({ servicio: s.name, tramo: t, precio, costo });
        }
      }
    }
    return malos;
  }, [grilla, ediciones]);

  function editar(serviceKey: string, tier: Tramo, valor: string) {
    setEdiciones((prev) => [
      ...prev.filter((e) => !(e.serviceKey === serviceKey && e.tier === tier)),
      { serviceKey, tier, valor },
    ]);
  }

  function valorDe(s: ServicioAdmin, t: Tramo): string {
    const e = ediciones.find((x) => x.serviceKey === s.key && x.tier === t);
    return e ? e.valor : (s.prices[t]?.price ?? '');
  }

  /** Sube toda una columna de golpe. Cuando el proveedor sube tarifas, sube todo junto. */
  function subirColumna(t: Tramo, pct: number) {
    if (!grilla) return;
    for (const s of grilla.services) {
      const actual = Number(valorDe(s, t));
      if (!Number.isFinite(actual) || actual <= 0) continue;
      editar(s.key, t, (actual * (1 + pct / 100)).toFixed(2));
    }
  }

  /**
   * Guarda los cambios, uno por uno.
   *
   * ── POR QUÉ EL ERROR NO SE PUEDE TRAGAR ─────────────────────────────────────
   *
   * Esto son PRECIOS. Si una llamada falla y nadie avisa, el admin cierra la pestaña
   * convencido de que subió la tarifa, y durante semanas se vende al precio viejo.
   *
   * Y como el guardado es secuencial, un fallo a mitad deja la grilla PARCIALMENTE
   * guardada. Eso hay que decirlo con esas palabras — "se guardaron 3 de 7" — porque un
   * "error al guardar" genérico haría pensar que no se guardó nada, y quien lo reintente
   * entero puede pisar algo que sí entró.
   */
  async function guardar() {
    if (!grilla) return;
    setGuardando(true);
    setAvisoGuardado(null);

    let hechos = 0;
    const total = ediciones.length + Object.keys(costos).length;

    try {
      for (const e of ediciones) {
        await api.fijarPrecio(e.serviceKey, e.tier, grilla.currency, e.valor);
        hechos++;
      }
      for (const [key, v] of Object.entries(costos)) {
        await api.fijarCosto(key, v === '' ? null : v);
        hechos++;
      }
      setEdiciones([]);
      setCostos({});
      setEstado({ fase: 'listo', grilla: await api.grillaAdmin('PEN') });
    } catch (e) {
      const detalle = e instanceof ErrorApi ? e.message : 'Error de red.';
      setAvisoGuardado(
        hechos === 0
          ? `No se guardó ningún cambio. ${detalle}`
          : `Se guardaron ${hechos} de ${total} cambios y el resto falló. ${detalle} Revisa la grilla antes de reintentar: los que ya entraron están aplicados.`,
      );
      // La grilla se recarga para que se vea qué entró de verdad. Dejar los valores
      // editados en pantalla haría creer que ninguno se guardó.
      await api
        .grillaAdmin('PEN')
        .then((g) => setEstado({ fase: 'listo', grilla: g }))
        .catch(() => undefined);
      setEdiciones([]);
      setCostos({});
    } finally {
      setGuardando(false);
    }
  }

  const hayCambios = ediciones.length > 0 || Object.keys(costos).length > 0;

  if (estado.fase === 'sinPermiso') {
    return (
      <main className="flex-1 p-8">
        <Tarjeta>
          {/*
            El mensaje NO dice qué hay del otro lado. "No puedes ver los márgenes de la
            empresa" le confirma a quien no debería estar aquí que aquí hay márgenes.
          */}
          <Vacio
            titulo="No tienes permiso para esta pantalla"
            texto="Es una consola interna. Si crees que deberías tener acceso, pídeselo a un administrador de tu organización."
          />
        </Tarjeta>
      </main>
    );
  }

  if (estado.fase === 'error') {
    return (
      <main className="flex-1 p-8">
        <Tarjeta>
          <Vacio titulo="No se pudo cargar" texto={estado.mensaje} />
        </Tarjeta>
      </main>
    );
  }

  if (!grilla) {
    return (
      <main className="flex-1 p-8">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="mt-4 h-64" />
      </main>
    );
  }

  return (
    <main className="flex-1 overflow-auto px-8 py-7">
      <header className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[19px] font-bold tracking-[-0.01em]">Precios y márgenes</h1>
          <p className="mt-0.5 text-[13px]" style={{ color: 'var(--text-2)' }}>
            Servicio × tramo · precio, costo y margen · se edita aquí, sin desplegar código.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="rounded-lg px-3 py-2 text-[12.5px] font-semibold"
            style={{ background: 'var(--surface)', border: '1px solid var(--border-2)' }}
          >
            Soles (PEN)
          </span>
          <Boton variante="primario" onClick={guardar} disabled={!hayCambios || guardando}>
            {guardando
              ? 'Guardando…'
              : `Guardar cambios${hayCambios ? ` (${ediciones.length + Object.keys(costos).length})` : ''}`}
          </Boton>
        </div>
      </header>

      {avisoGuardado ? (
        <div className="mb-3">
          <AvisoError mensaje={avisoGuardado} reembolsado={false} />
        </div>
      ) : null}

      {/* Los pendientes van ARRIBA, no escondidos: son una tarea, no un detalle. */}
      {sinCosto.length > 0 ? (
        <div
          className="mb-3 rounded-lg px-4 py-2.5 text-[13px]"
          style={{ background: 'var(--warn-weak)', color: 'var(--warn)' }}
        >
          <strong>
            {sinCosto.length} servicio{sinCosto.length > 1 ? 's' : ''} sin costo
          </strong>{' '}
          — pídeselo al proveedor: {sinCosto.map((s) => s.name).join(', ')}.
        </div>
      ) : null}

      {bajoCosto.length > 0 ? (
        <div
          className="mb-3 rounded-lg px-4 py-2.5 text-[13px]"
          style={{ background: 'var(--bad-weak)', color: 'var(--bad)' }}
          role="alert"
        >
          <strong>
            {bajoCosto.length} precio{bajoCosto.length > 1 ? 's' : ''} por debajo del costo.
          </strong>{' '}
          {bajoCosto.map((b) => (
            <span key={`${b.servicio}-${b.tramo}`}>
              {b.servicio} en {b.tramo}: vendes a S/ {b.precio.toFixed(2)} y te cuesta S/{' '}
              {b.costo.toFixed(2)} — pierdes S/ {(b.costo - b.precio).toFixed(2)} en cada consulta.{' '}
            </span>
          ))}
        </div>
      ) : null}

      <div
        className="overflow-hidden rounded-xl"
        style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}
      >
        <table className="w-full text-[13px]">
          <thead>
            <tr style={{ background: 'var(--surface-2)', color: 'var(--text-3)' }}>
              <th className="px-4 py-2.5 text-left text-[12px] font-normal">Servicio</th>
              <th className="px-3 py-2.5 text-right text-[12px] font-normal">Costo proveedor</th>
              {TRAMOS.map((t) => (
                <th key={t} className="px-3 py-2.5 text-right text-[12px] font-normal">
                  <div className="flex items-center justify-end gap-1.5">
                    {t}
                    <button
                      onClick={() => subirColumna(t, 10)}
                      className="rounded px-1.5 py-0.5 text-[10px] font-semibold"
                      style={{ background: 'var(--accent-weak)', color: 'var(--accent)' }}
                      title={`Subir 10% toda la columna ${t}`}
                    >
                      +10%
                    </button>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grilla.services.map((s) => {
              const costoActual = costos[s.key] ?? s.cost ?? '';
              return (
                <tr key={s.key} style={{ borderTop: '1px solid var(--border)' }}>
                  <td className="px-4 py-2.5">
                    <div className="font-medium">{s.name}</div>
                    <div className="mono text-[11px]" style={{ color: 'var(--text-3)' }}>
                      {s.key}
                    </div>
                  </td>

                  <td className="px-3 py-2.5 text-right">
                    <input
                      value={costoActual}
                      onChange={(e) =>
                        setCostos((p) => ({ ...p, [s.key]: e.target.value.replace(/[^\d.]/g, '') }))
                      }
                      placeholder="sin costo"
                      className="tabular w-[86px] rounded px-2 py-1 text-right text-[13px] outline-none"
                      style={{
                        background: s.costKnown ? 'transparent' : 'var(--warn-weak)',
                        border: '1px solid var(--border)',
                        color: 'var(--text)',
                      }}
                    />
                  </td>

                  {TRAMOS.map((t) => {
                    const celda = s.prices[t];
                    const valor = valorDe(s, t);
                    const editada = ediciones.some((e) => e.serviceKey === s.key && e.tier === t);
                    const costoNum = Number(costoActual);
                    const conocido = costoActual !== '' && Number.isFinite(costoNum);
                    const precioNum = Number(valor);
                    const margen =
                      conocido && valor !== '' && Number.isFinite(precioNum)
                        ? precioNum - costoNum
                        : null;

                    return (
                      <td key={t} className="px-3 py-2.5 text-right">
                        <input
                          value={valor}
                          onChange={(e) => editar(s.key, t, e.target.value.replace(/[^\d.]/g, ''))}
                          placeholder="—"
                          className="tabular w-[76px] rounded px-2 py-1 text-right text-[13px] outline-none"
                          style={{
                            background: editada ? 'var(--accent-weak)' : 'transparent',
                            border: `1px solid ${editada ? 'var(--accent)' : 'var(--border)'}`,
                            color: 'var(--text)',
                          }}
                        />
                        {/*
                          Aquí está la regla. Sin costo conocido -> NO se pinta un margen.
                          Ni "0.00", ni "—" en el mismo lugar donde iría un número: nada, y
                          la etiqueta dice por qué.
                        */}
                        <div
                          className="tabular mt-0.5 text-[11px]"
                          style={{ color: 'var(--text-3)' }}
                        >
                          {margen !== null ? (
                            <span
                              style={
                                margen < 0 ? { color: 'var(--bad)', fontWeight: 600 } : undefined
                              }
                            >
                              margen {margen.toFixed(2)}
                            </span>
                          ) : (
                            <span style={{ fontStyle: 'italic' }}>sin costo</span>
                          )}
                        </div>
                        {celda?.marginPct !== null && celda?.marginPct !== undefined && !editada ? (
                          <div className="tabular text-[10px]" style={{ color: 'var(--text-3)' }}>
                            {celda.marginPct}%
                          </div>
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-[12.5px]" style={{ color: 'var(--text-3)' }}>
        Cuando no conocemos el costo, el margen queda{' '}
        <strong style={{ color: 'var(--text-2)' }}>vacío — nunca cero</strong>. Un margen calculado
        sobre un costo inventado hace que alguien fije un precio con ese número y venda por debajo
        del costo durante meses sin que nadie lo note.
      </p>
    </main>
  );
}
